import React, { useState } from "react";
import { Calendar, MapPin, ExternalLink, ArrowRight, Wifi } from "lucide-react";
import PageHero from "@/components/PageHero";
import FloatingEditButton from "@/components/FloatingEditButton";
import EnrollmentModal from "@/components/EnrollmentModal";
import ShareButtons from "@/components/ShareButtons";
import { AlbumCompacto } from "@/components/GaleriaFotos";
import CarouselSection from "@/components/CarouselSection";
import SEO from "@/components/SEO";
import Reveal from "@/components/motion/Reveal";
import Section, { SectionHeading } from "@/components/ui/Section";
import Card, { CardBody } from "@/components/ui/Card";
import CTAButton from "@/components/ui/CTAButton";
import FilterPills from "@/components/ui/FilterPills";
import SmartImage from "@/components/ui/SmartImage";
import EmptyState from "@/components/ui/EmptyState";
import { CardGridSkeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";
import useCachedResource from "@/hooks/useCachedResource";

const categoryColors = {
  evento: "bg-blue-100 text-blue-700",
  taller: "bg-purple-100 text-purple-700",
  jornada: "bg-amber-100 text-amber-700",
  asamblea: "bg-green-100 text-green-700",
  curso: "bg-red-100 text-red-700",
};

const modalityColors = {
  presencial: "bg-green-100 text-green-700",
  online: "bg-blue-100 text-blue-700",
  hibrido: "bg-purple-100 text-purple-700",
};
const modalityLabels = { presencial: "Presencial", online: "Online", hibrido: "Híbrido" };

const retreatFeatures = [
  { icon: "🙏", title: "Oración y Silencio", desc: "Tiempos de lectio divina, adoración y contemplación en el mundo digital." },
  { icon: "💡", title: "Reflexión Pastoral", desc: "Discernimiento sobre la presencia cristiana en Internet y las redes." },
  { icon: "🌐", title: "Modalidad Híbrida", desc: "Propuestas presenciales, online y mixtas para facilitar la participación." },
];

export default function Eventos() {
  const { data: events, loading } = useCachedResource("events", () => apiGet("/events"));
  const { data: retreats, loading: loadingRetreats } = useCachedResource("retreats", () => apiGet("/retreats"));
  const [activeCategory, setActiveCategory] = useState("todos");
  const [enrollModal, setEnrollModal] = useState(null); // { id, title, type }

  const eventos = Array.isArray(events) ? events : [];
  const retiros = Array.isArray(retreats) ? retreats : [];

  const categories = ["todos", ...new Set(eventos.map(e => e.category))];
  const filtered = activeCategory === "todos" ? eventos : eventos.filter(e => e.category === activeCategory);

  return (
    <div data-testid="eventos-page">
      <SEO
        title="Eventos, cursos y encuentros diocesanos"
        description="Talleres, jornadas, asambleas, retiros digitales y encuentros de evangelizadores digitales de la Diócesis de Huelva."
      />
      <PageHero slug="eventos" />

      {/* Filtros de categoría.
          Antes toda la barra estaba condicionada a `!loading`, así que aparecía
          de golpe al terminar la carga y empujaba el carrusel hacia abajo. */}
      {categories.length > 2 && (
        <section className="section-bg-light px-6 md:px-12 pt-16">
          <div className="max-w-7xl mx-auto">
            <FilterPills
              options={categories.map(cat => ({
                value: cat,
                label: cat === "todos" ? "Todos" : cat.charAt(0).toUpperCase() + cat.slice(1),
              }))}
              value={activeCategory}
              onChange={setActiveCategory}
              label="Filtrar eventos por categoría"
              data-testid="events-category-filter"
            />
          </div>
        </section>
      )}

      {/* Eventos */}
      {loading && !eventos.length ? (
        <Section tone="light">
          <SectionHeading label="Agenda" title="Próximos Eventos" />
          <CardGridSkeleton count={3} />
        </Section>
      ) : filtered.length === 0 ? (
        <Section tone="light">
          <EmptyState
            icon={Calendar}
            title="No hay eventos disponibles"
            description="Próximamente anunciaremos nuevas actividades. Síguenos en redes para estar al día."
            action={<CTAButton to="/contacto">Contactar para más información</CTAButton>}
          />
        </Section>
      ) : (
        <CarouselSection label="Agenda" title="Próximos Eventos" tone="light" testId="events-section" maxItems={7}>
          {filtered.map((ev, i) => (
            <Card key={ev._id || i} data-testid={`event-item-${i}`} className="flex flex-col h-full" interactive>
              <SmartImage
                src={ev.image}
                alt=""
                ratio="16 / 9"
                fallback={
                  <div className="w-full h-full flex items-center justify-center bg-[#f5f5f7]">
                    <Calendar className="w-10 h-10 text-foreground/15" aria-hidden="true" />
                  </div>
                }
              />
              <CardBody className="flex flex-col flex-1">
                <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-3 w-fit capitalize ${categoryColors[ev.category] || "bg-muted text-muted-foreground"}`}>
                  {ev.category}
                </span>
                <h3 className="font-heading font-bold text-base mb-2">{ev.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">{ev.description}</p>
                <div className="space-y-1.5 mb-5 border-t border-border pt-4">
                  {ev.date && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="w-3.5 h-3.5 text-primary" aria-hidden="true" />{ev.date}
                      {ev.time && <span>· {ev.time}</span>}
                    </div>
                  )}
                  {ev.location && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5 text-primary" aria-hidden="true" />{ev.location}
                    </div>
                  )}
                </div>
                <AlbumCompacto fotos={ev.gallery} className="mb-4" />
                {ev.registration_url ? (
                  <CTAButton href={ev.registration_url} size="sm" className="font-semibold w-full"
                    data-testid={`event-register-${i}`}>
                    Inscribirse <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                  </CTAButton>
                ) : (
                  <CTAButton
                    onClick={() => setEnrollModal({ id: ev.id || ev._id, title: ev.title, type: "evento" })}
                    size="sm"
                    className="font-semibold w-full"
                    data-testid={`event-enroll-btn-${i}`}
                  >
                    Inscribirme <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                  </CTAButton>
                )}
                <div className="mt-4 pt-3 border-t border-border flex justify-end">
                  <ShareButtons
                    compact
                    url="/eventos"
                    title={ev.title}
                    description={ev.description || ""}
                  />
                </div>
              </CardBody>
            </Card>
          ))}
        </CarouselSection>
      )}

      {/* Retiros digitales: presentación */}
      <Section tone="muted" data-testid="retiros-intro">
        <Reveal>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary mb-3">¿Qué son?</p>
            <h2 className="font-heading font-bold mb-6 text-balance" style={{ fontSize: "var(--text-h2)" }}>
              Silencio y Misión en el Mundo Digital
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Los retiros digitales son espacios de oración, silencio y reflexión diseñados
              especialmente para evangelizadores que viven en el mundo digital. Son momentos para
              parar, escuchar a Dios y renovar la motivación misionera.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Combinamos la espiritualidad cristiana tradicional con la reflexión sobre la presencia
              y el testimonio en el entorno digital. Una propuesta pastoral única y necesaria.
            </p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-6 mt-12">
          {retreatFeatures.map((f, i) => (
            <Reveal key={i} delay={i * 70} distance={24}>
              <Card className="p-6 text-center h-full" interactive>
                <div className="text-3xl mb-3" aria-hidden="true">{f.icon}</div>
                <h3 className="font-heading font-bold text-base mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </Card>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Retiros disponibles */}
      {loadingRetreats && !retiros.length ? (
        <Section tone="muted" space="tight">
          <CardGridSkeleton count={2} />
        </Section>
      ) : retiros.length === 0 ? (
        <Section tone="muted" space="tight">
          <EmptyState
            icon={Wifi}
            title="Próximamente nuevos retiros"
            description="Estamos preparando las siguientes convocatorias."
            action={<CTAButton to="/contacto" variant="outline">Contactar para más información</CTAButton>}
          />
        </Section>
      ) : (
        <CarouselSection
          label="Retiros Digitales"
          title="Retiros Disponibles"
          description="Espacios de encuentro, oración y silencio para evangelizadores digitales."
          tone="muted"
          testId="retiros-section"
        >
          {retiros.map((r, i) => (
            <Card key={r._id || i} data-testid={`retreat-card-${i}`} className="flex flex-col h-full" interactive>
              <SmartImage
                src={r.image}
                alt=""
                ratio="16 / 9"
                fallback={
                  <div className="w-full h-full flex items-center justify-center bg-[#f5f5f7]">
                    <Wifi className="w-10 h-10 text-foreground/15" aria-hidden="true" />
                  </div>
                }
              />
              <CardBody className="flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${modalityColors[r.modality] || "bg-muted text-muted-foreground"}`}>
                    {modalityLabels[r.modality] || r.modality}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{r.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">{r.description}</p>
                <div className="space-y-1.5 mb-5 pt-4 border-t border-border">
                  {r.date && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Calendar className="w-3.5 h-3.5 text-primary" aria-hidden="true" />{r.date}</div>}
                  {r.duration && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Wifi className="w-3.5 h-3.5 text-primary" aria-hidden="true" />{r.duration}</div>}
                  {r.location && <div className="flex items-center gap-2 text-xs text-muted-foreground"><MapPin className="w-3.5 h-3.5 text-primary" aria-hidden="true" />{r.location}</div>}
                </div>
                <AlbumCompacto fotos={r.gallery} className="mb-4" />
                {r.registration_url ? (
                  <CTAButton href={r.registration_url} size="sm" className="font-semibold w-full"
                    data-testid={`retreat-register-${i}`}>
                    Inscribirse <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                  </CTAButton>
                ) : (
                  <CTAButton
                    onClick={() => setEnrollModal({ id: r.id || r._id, title: r.title, type: "retiro" })}
                    size="sm"
                    className="font-semibold w-full"
                    data-testid={`retreat-enroll-btn-${i}`}
                  >
                    Inscribirme <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                  </CTAButton>
                )}
              </CardBody>
            </Card>
          ))}
        </CarouselSection>
      )}

      {/* Propuesta de evento */}
      <Section tone="light" width="narrow" space="tight" innerClassName="text-center">
        <Reveal>
          <h2 className="font-heading text-2xl font-bold mb-3">¿Organizas un evento?</h2>
          <p className="text-muted-foreground mb-8">
            Si quieres proponer un encuentro o taller, escríbenos y lo coordinamos juntos.
          </p>
          <div className="flex justify-center">
            <CTAButton to="/contacto" className="font-semibold">
              Proponer evento <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </CTAButton>
          </div>
        </Reveal>
      </Section>

      {/* Retiro a medida */}
      <Section tone="dark" width="narrow" space="tight" innerClassName="text-center">
        <Reveal>
          <h2 className="font-heading text-2xl font-bold mb-3">¿Tu comunidad necesita un retiro?</h2>
          <p className="text-white/70 mb-8">
            Podemos organizar retiros personalizados para tu parroquia, movimiento o grupo juvenil.
          </p>
          <div className="flex justify-center">
            <CTAButton to="/contacto" variant="light" className="font-semibold">
              Solicitar retiro personalizado <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </CTAButton>
          </div>
        </Reveal>
      </Section>

      <FloatingEditButton slug="eventos" />
      {enrollModal && (
        <EnrollmentModal
          itemId={enrollModal.id}
          itemTitle={enrollModal.title}
          itemType={enrollModal.type}
          onClose={() => setEnrollModal(null)}
        />
      )}
    </div>
  );
}
